import { buttonClasses, Menu as MuiMenu, type MenuProps, useTheme } from '@mui/material';
import { Children, cloneElement, type ReactElement, useId, useState } from 'react';

type Props = {
  open?: boolean;
  setOpen?: (newState: boolean) => void;
  button: ReactElement;
  children: ReactElement[];
  equalMenuWidth?: boolean;
} & Omit<MenuProps, 'open' | 'anchorEl' | 'id' | 'children'>;

export const Menu = ({
  open: controlledOpen,
  setOpen: setControlledOpen,
  button,
  children,
  equalMenuWidth = false,
  ...rest
}: Props) => {
  const menuButtonId = useId();
  const menuId = useId();
  const theme = useTheme();
  const [internalOpen, setInternalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isControlled = controlledOpen !== undefined && setControlledOpen !== undefined;

  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? setControlledOpen : setInternalOpen;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (button.props.onClick) {
      button.props.onClick(event);
    }
    handleClick(event);
  };

  const menuButton = cloneElement(button, {
    ...button.props,
    'aria-controls': open ? menuId : undefined,
    'aria-expanded': open ? 'true' : undefined,
    'aria-haspopup': 'true',
    id: menuButtonId,
    onClick: onButtonClick,
    sx: {
      '&[aria-expanded="true"]': {
        borderColor: theme.palette.primary.main,
      },
      [`&:${buttonClasses.text}`]: {
        border: `1px solid transparent`,
      },
    },
  });

  const menuItems = Children.map(children, (child) => {
    const onClick = (event: React.MouseEvent<HTMLElement>) => {
      if (child.props.onClick) {
        child.props.onClick(event);
      }
      setOpen(false);
    };

    return cloneElement(child, { onClick });
  });

  return (
    <div>
      {menuButton}
      <MuiMenu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': menuButtonId,
          sx: {
            width: equalMenuWidth && anchorEl ? anchorEl.offsetWidth : undefined, // makes menu items the same width as the button
          },
        }}
        {...rest}
      >
        {menuItems}
      </MuiMenu>
    </div>
  );
};
